import { useCallback, useEffect, useState } from "react";
import api, { API_ORIGIN } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";

// ---- Read helpers -----------------------------------------------------------
async function safeGet(path) {
  try {
    const { data } = await api.get(path);
    return Array.isArray(data) ? data : data || [];
  } catch {
    return [];
  }
}

// Build a map of media_asset_id -> full absolute content URL. The Media Engine
// returns a relative `signed_url` (/api/v1/media/{id}/content?token=…); we
// prefix API_ORIGIN so <img> can load it. Variant (e.g. "thumbnail") is appended.
export function mediaUrl(mediaMap, mediaAssetId, variant) {
  const signed = mediaMap?.[mediaAssetId];
  if (!signed) return null;
  let url = `${API_ORIGIN}${signed}`;
  if (variant) url += `&variant=${variant}`;
  return url;
}

// Fetch a fresh media_asset_id -> signed_url map (used after a generation to
// resolve the just-produced asset's bytes without reloading the whole page).
export async function fetchMediaMap() {
  const media = await safeGet("/media?limit=500");
  return Object.fromEntries((media || []).map((m) => [m.id, m.signed_url]));
}

// ---- Aggregate data hook ----------------------------------------------------
// Single source for every Creative Studio page — jobs, assets, providers,
// brand kits, templates + a media signed-url map to render generated bytes.
export function useCreativeData() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({
    jobs: [], assets: [], providers: [], brandKits: [], templates: [],
    destinations: [], publishRequests: [], mediaMap: {},
    analytics: { total_cost: null, currency: "USD" }, loading: true,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [jobs, assets, providers, brandKits, templates, media, analytics, destinations, publishRequests] = await Promise.all([
      safeGet("/creative/jobs"),
      safeGet("/creative/assets"),
      safeGet("/creative/providers"),
      safeGet("/creative/brand-kits"),
      safeGet("/creative/templates"),
      safeGet("/media?limit=500"),
      api.get("/creative/analytics").then((r) => r.data).catch(() => ({ total_cost: null, currency: "USD" })),
      safeGet("/creative/destinations"),
      safeGet("/creative/publish-requests"),
    ]);
    const mediaMap = Object.fromEntries((media || []).map((m) => [m.id, m.signed_url]));
    setState({ jobs, assets, providers, brandKits, templates, mediaMap, analytics, destinations, publishRequests, loading: false });
  }, []);

  useEffect(() => {
    if (currentOrg) load();
  }, [currentOrg, load]);

  return { ...state, reload: load };
}

// ---- Write / action API -----------------------------------------------------
export const creativeApi = {
  generate: (brief, idempotencyKey) =>
    api.post("/creative/generate", brief, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
    }).then((r) => r.data),
  preview: (brief) => api.post("/creative/preview", brief).then((r) => r.data),
  getJob: (id) => api.get(`/creative/jobs/${id}`).then((r) => r.data),
  cancelJob: (id) => api.post(`/creative/jobs/${id}/cancel`).then((r) => r.data),
  listAssets: () => api.get("/creative/assets").then((r) => r.data),

  createBrandKit: (body) => api.post("/creative/brand-kits", body).then((r) => r.data),
  updateBrandKit: (id, body) => api.put(`/creative/brand-kits/${id}`, body).then((r) => r.data),
  deleteBrandKit: (id) => api.delete(`/creative/brand-kits/${id}`).then((r) => r.data),

  createTemplate: (body) => api.post("/creative/templates", body).then((r) => r.data),
  updateTemplate: (id, body) => api.put(`/creative/templates/${id}`, body).then((r) => r.data),
  deleteTemplate: (id) => api.delete(`/creative/templates/${id}`).then((r) => r.data),

  // Approve / Publish pipeline (SPRINT-103)
  approveAsset: (id, note) => api.post(`/creative/assets/${id}/approve`, { note }).then((r) => r.data),
  rejectAsset: (id, note) => api.post(`/creative/assets/${id}/reject`, { note }).then((r) => r.data),
  archiveAsset: (id, note) => api.post(`/creative/assets/${id}/archive`, { note }).then((r) => r.data),
  getTimeline: (id) => api.get(`/creative/assets/${id}/timeline`).then((r) => r.data),
  getAssetDetails: (id) => api.get(`/creative/assets/${id}/details`).then((r) => r.data),
  publish: (body) => api.post("/creative/publish", body).then((r) => r.data),
  listPublishRequests: (params) => api.get("/creative/publish-requests", { params }).then((r) => r.data),
  cancelPublishRequest: (id) => api.post(`/creative/publish-requests/${id}/cancel`).then((r) => r.data),

  listDestinations: () => api.get("/creative/destinations").then((r) => r.data),
  createDestination: (body) => api.post("/creative/destinations", body).then((r) => r.data),
  updateDestination: (id, body) => api.put(`/creative/destinations/${id}`, body).then((r) => r.data),
  deleteDestination: (id) => api.delete(`/creative/destinations/${id}`).then((r) => r.data),
};

// AI Provider Marketplace (SPRINT-105.4) — per-org provider management.
// + AI Provider Routing Intelligence (SPRINT-105.5) — policy + dry-run preview.
export const marketplaceApi = {
  get: () => api.get("/creative/marketplace").then((r) => r.data),
  analytics: () => api.get("/creative/marketplace/analytics").then((r) => r.data),
  saveSettings: (pid, body) => api.put(`/creative/marketplace/${pid}/settings`, body).then((r) => r.data),
  setApiKey: (pid, api_key) => api.put(`/creative/marketplace/${pid}/api-key`, { api_key }).then((r) => r.data),
  test: (pid) => api.post(`/creative/marketplace/${pid}/test`).then((r) => r.data),
  validate: (pid) => api.post(`/creative/marketplace/${pid}/validate`).then((r) => r.data),
  getPolicy: () => api.get("/creative/routing/policy").then((r) => r.data),
  setPolicy: (policy) => api.put("/creative/routing/policy", { policy }).then((r) => r.data),
  routingPreview: (body) => api.post("/creative/routing/preview", body).then((r) => r.data),
};

// SPRINT-PH-14 — Image Editors (separate category from generation).
export const imageEditorApi = {
  list: () => api.get("/creative/image-editors").then((r) => r.data),
  setDefault: (provider_id) => api.put("/creative/image-editors/default", { provider_id }).then((r) => r.data),
  edit: (body) => api.post("/creative/image-editor/edit", body).then((r) => r.data),
};


// Visual Prompt Builder (SPRINT-105.7) — presets/templates, favorites, history.
export const promptApi = {
  render: (structured, kind = "image") => api.post("/creative/prompt/render", { structured, kind }).then((r) => r.data),
  compose: (description, kind = "image") => api.post("/creative/prompt/compose", { description, kind }).then((r) => r.data),
  assist: (body) => api.post("/creative/prompt/assist", body).then((r) => r.data),
  listPresets: (favoritesOnly = false) => api.get(`/creative/prompt-presets${favoritesOnly ? "?favorites_only=true" : ""}`).then((r) => r.data),
  createPreset: (body) => api.post("/creative/prompt-presets", body).then((r) => r.data),
  updatePreset: (id, patch) => api.put(`/creative/prompt-presets/${id}`, patch).then((r) => r.data),
  toggleFavorite: (id) => api.post(`/creative/prompt-presets/${id}/favorite`).then((r) => r.data),
  deletePreset: (id) => api.delete(`/creative/prompt-presets/${id}`).then((r) => r.data),
  listHistory: (limit = 50) => api.get(`/creative/prompt-history?limit=${limit}`).then((r) => r.data),
  clearHistory: () => api.delete("/creative/prompt-history").then((r) => r.data),
};

// AI Video Studio (SPRINT-105.8) — Projects → Storyboard → Scenes.
export const videoApi = {
  listProjects: (archived = false) => api.get(`/creative/video/projects${archived ? "?archived=true" : ""}`).then((r) => r.data),
  getProject: (id) => api.get(`/creative/video/projects/${id}`).then((r) => r.data),
  createProject: (body) => api.post("/creative/video/projects", body).then((r) => r.data),
  updateProject: (id, patch) => api.put(`/creative/video/projects/${id}`, patch).then((r) => r.data),
  saveProject: (id) => api.post(`/creative/video/projects/${id}/save`).then((r) => r.data),
  duplicateProject: (id) => api.post(`/creative/video/projects/${id}/duplicate`).then((r) => r.data),
  archiveProject: (id, archived = true) => api.post(`/creative/video/projects/${id}/archive?archived=${archived}`).then((r) => r.data),
  deleteProject: (id) => api.delete(`/creative/video/projects/${id}`).then((r) => r.data),
  listVersions: (id) => api.get(`/creative/video/projects/${id}/versions`).then((r) => r.data),
  restoreVersion: (id, version) => api.post(`/creative/video/projects/${id}/versions/${version}/restore`).then((r) => r.data),
  generateScene: (id, sceneId) => api.post(`/creative/video/projects/${id}/scenes/${sceneId}/generate`).then((r) => r.data),
};

// AI Campaign Orchestrator (SPRINT-106.0) — strategic coordination layer.
export const campaignApi = {
  list: (archived = false) => api.get(`/creative/campaigns/plans${archived ? "?archived=true" : ""}`).then((r) => r.data),
  get: (id) => api.get(`/creative/campaigns/plans/${id}`).then((r) => r.data),
  create: (body) => api.post("/creative/campaigns/plans", body).then((r) => r.data),
  update: (id, patch) => api.put(`/creative/campaigns/plans/${id}`, patch).then((r) => r.data),
  setStatus: (id, status) => api.post(`/creative/campaigns/plans/${id}/status`, { status }).then((r) => r.data),
  save: (id) => api.post(`/creative/campaigns/plans/${id}/save`).then((r) => r.data),
  execute: (id) => api.post(`/creative/campaigns/plans/${id}/execute`).then((r) => r.data),
  versions: (id) => api.get(`/creative/campaigns/plans/${id}/versions`).then((r) => r.data),
  history: (id) => api.get(`/creative/campaigns/plans/${id}/history`).then((r) => r.data),
  remove: (id) => api.delete(`/creative/campaigns/plans/${id}`).then((r) => r.data),
};

export function useMarketplace() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ installed: [], catalog: [], policy: "balanced", policies: [], loading: true });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [d, pol] = await Promise.all([
        marketplaceApi.get(),
        marketplaceApi.getPolicy().catch(() => ({ policy: "balanced", available: [] })),
      ]);
      setState({
        installed: d.installed || [], catalog: d.catalog || [],
        policy: pol.policy || d.policy || "balanced", policies: pol.available || [], loading: false,
      });
    } catch {
      setState({ installed: [], catalog: [], policy: "balanced", policies: [], loading: false });
    }
  }, []);
  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);
  return { ...state, reload: load };
}

// ---- Delivery Engine (SPRINT-104) — platform delivery monitor/queue/details.
export const deliveryApi = {
  listJobs: (params) => api.get("/delivery/jobs", { params }).then((r) => r.data),
  getJob: (id) => api.get(`/delivery/jobs/${id}`).then((r) => r.data),
  stats: () => api.get("/delivery/stats").then((r) => r.data),
  retry: (id) => api.post(`/delivery/jobs/${id}/retry`).then((r) => r.data),
  cancel: (id) => api.post(`/delivery/jobs/${id}/cancel`).then((r) => r.data),
};

// Poll a job to a terminal state (provider-agnostic: sync providers already
// return "completed"; async providers report "queued"/"running" until done).
const TERMINAL = ["completed", "failed", "cancelled", "dead_letter"];
export async function pollJobUntilDone(jobId, { interval = 1500, timeout = 120000 } = {}) {
  const start = Date.now();
  let job = await creativeApi.getJob(jobId);
  while (!TERMINAL.includes(job.status) && Date.now() - start < timeout) {
    await new Promise((r) => setTimeout(r, interval));
    job = await creativeApi.getJob(jobId);
  }
  return job;
}
